import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { API_BASE_URL } from '../config/api';
import { formatTrainingLevel } from '../utils/popupQuestionUtils';

const PopupQuestionManager = () => {
  const { currentUser, setIsPopupQuestionLoading } = useApp();
  const location = useLocation();
  const [question, setQuestion] = useState(null);
  const [answer, setAnswer] = useState('');
  const [selectedChoice, setSelectedChoice] = useState('');
  const [submittedAnswer, setSubmittedAnswer] = useState(null);
  const [isGraphicExpanded, setIsGraphicExpanded] = useState(false);
  const [answerSecondsRemaining, setAnswerSecondsRemaining] = useState(30);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const requestInFlight = useRef(false);
  const questionOpen = useRef(false);
  const isOptedOut = useRef(false);
  const previousSessionId = useRef(null);
  const previousPath = useRef(location.pathname);
  const answerCloseTimeout = useRef(null);
  const answerCountdownInterval = useRef(null);

  useEffect(() => () => {
    if (answerCloseTimeout.current) clearTimeout(answerCloseTimeout.current);
    if (answerCountdownInterval.current) clearInterval(answerCountdownInterval.current);
  }, []);

  useEffect(() => {
    if (!isGraphicExpanded) return undefined;
    const handleEscape = (event) => {
      if (event.key === 'Escape') setIsGraphicExpanded(false);
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isGraphicExpanded]);

  const requestPopupQuestion = useCallback(async () => {
    const sessionId = currentUser?.sessionId;
    if (!sessionId || requestInFlight.current || questionOpen.current || isOptedOut.current) return;

    requestInFlight.current = true;
    setIsPopupQuestionLoading(true);
    setErrorMessage('');
    try {
      const settingsResponse = await fetch(`${API_BASE_URL}/popup-question/settings`, {
        headers: { 'session-id': sessionId }
      });
      if (!settingsResponse.ok) return;
      const preference = await settingsResponse.json();
      if (preference.popupQuestionsOptOut) {
        isOptedOut.current = true;
        return;
      }

      const response = await fetch(`${API_BASE_URL}/popup-question/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': sessionId
        },
        body: JSON.stringify({})
      });
      if (!response.ok) return;
      const generatedQuestion = await response.json();
      questionOpen.current = true;
      setQuestion(generatedQuestion);
      setAnswer('');
      setSelectedChoice('');
      setSubmittedAnswer(null);
    } catch (error) {
      console.error('Unable to load popup question:', error);
    } finally {
      requestInFlight.current = false;
      setIsPopupQuestionLoading(false);
    }
  }, [currentUser?.sessionId]);

  useEffect(() => {
    const sessionId = currentUser?.sessionId || null;
    const sessionChanged = sessionId !== previousSessionId.current;
    const enteredCalendar = location.pathname === '/calendar' && previousPath.current !== '/calendar';
    previousSessionId.current = sessionId;
    previousPath.current = location.pathname;

    if (!sessionId) {
      if (answerCloseTimeout.current) {
        clearTimeout(answerCloseTimeout.current);
        answerCloseTimeout.current = null;
      }
      if (answerCountdownInterval.current) {
        clearInterval(answerCountdownInterval.current);
        answerCountdownInterval.current = null;
      }
      questionOpen.current = false;
      isOptedOut.current = false;
      setIsPopupQuestionLoading(false);
      setQuestion(null);
      setSubmittedAnswer(null);
      return;
    }

    if (sessionChanged) {
      if (answerCloseTimeout.current) {
        clearTimeout(answerCloseTimeout.current);
        answerCloseTimeout.current = null;
      }
      if (answerCountdownInterval.current) {
        clearInterval(answerCountdownInterval.current);
        answerCountdownInterval.current = null;
      }
      questionOpen.current = false;
      isOptedOut.current = false;
      setQuestion(null);
      setSubmittedAnswer(null);
    }
    if (sessionChanged || enteredCalendar) {
      requestPopupQuestion();
    }
  }, [currentUser?.sessionId, location.pathname, requestPopupQuestion]);

  useEffect(() => {
    const handleScheduleDayViewed = () => requestPopupQuestion();
    window.addEventListener('popup-question:schedule-day-viewed', handleScheduleDayViewed);
    return () => window.removeEventListener('popup-question:schedule-day-viewed', handleScheduleDayViewed);
  }, [requestPopupQuestion]);

  const dismissQuestion = () => {
    if (answerCloseTimeout.current) {
      clearTimeout(answerCloseTimeout.current);
      answerCloseTimeout.current = null;
    }
    if (answerCountdownInterval.current) {
      clearInterval(answerCountdownInterval.current);
      answerCountdownInterval.current = null;
    }
    questionOpen.current = false;
    setQuestion(null);
    setSubmittedAnswer(null);
    setIsGraphicExpanded(false);
    setAnswerSecondsRemaining(30);
    setErrorMessage('');
  };

  const submitAnswer = async () => {
    const userAnswer = question.answerType === 'multiple_choice'
      ? (selectedChoice ? Number(selectedChoice) : null)
      : answer.trim();
    if (userAnswer == null || userAnswer === '') return;

    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/popup-question/${question.id}/answer`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ userAnswer })
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error || 'Unable to submit your answer');
      }
      const result = await response.json();
      setSubmittedAnswer(result);
      setAnswerSecondsRemaining(30);
      answerCloseTimeout.current = setTimeout(dismissQuestion, 30000);
      answerCountdownInterval.current = setInterval(() => {
        setAnswerSecondsRemaining((remaining) => Math.max(remaining - 1, 0));
      }, 1000);
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const optOut = async () => {
    setIsSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(`${API_BASE_URL}/popup-question/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'session-id': currentUser?.sessionId
        },
        body: JSON.stringify({ popupQuestionsOptOut: true })
      });
      if (!response.ok) {
        const result = await response.json().catch(() => null);
        throw new Error(result?.error || 'Unable to turn off popup questions');
      }
      isOptedOut.current = true;
      dismissQuestion();
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!question) return null;

  const canSubmit = question.answerType === 'multiple_choice'
    ? !submittedAnswer && !!selectedChoice
    : !submittedAnswer && !!answer.trim();
  const requiresAnswer = question.requiresAnswer !== false;
  const contentTypeLabel = question.contentType === 'information' ? 'Information' : 'Practice question';
  const displayTitle = question.title || 'Practice Question';
  const showQuestionText = question.question && question.question.trim() !== displayTitle.trim();
  const graphic = question.graphic && typeof question.graphic === 'object' ? question.graphic : null;

  return (
    <div role="presentation" onMouseDown={(event) => event.stopPropagation()} style={{ position: 'fixed', inset: 0, zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', backgroundColor: 'rgba(15, 34, 45, 0.62)' }}>
      <section role="dialog" aria-modal="true" aria-labelledby="popup-question-title" style={{ position: 'relative', width: 'min(620px, 100%)', maxHeight: '90vh', overflowY: 'auto', padding: '1.5rem', backgroundColor: '#fff', borderRadius: '10px', boxShadow: '0 18px 56px rgba(0, 0, 0, 0.28)' }}>
        <button type="button" onClick={dismissQuestion} aria-label="Close practice question" title="Close" style={{ position: 'absolute', top: '0.55rem', right: '0.65rem', width: '34px', height: '34px', display: 'grid', placeItems: 'center', color: '#526671', backgroundColor: 'transparent', border: 0, borderRadius: '5px', fontSize: '1.6rem', lineHeight: 1, cursor: 'pointer' }}>×</button>
        <div style={{ marginBottom: '1.25rem' }}>
          <div style={{ marginBottom: '0.35rem', color: '#527080', fontSize: '0.8rem', fontWeight: 700, textTransform: 'uppercase' }}>
            {question.trainingLevel ? `${formatTrainingLevel(question.trainingLevel)} practice` : contentTypeLabel}
            {question.questionStyle && ` · ${question.questionStyle.replaceAll('_', ' ')}`}
          </div>
          <h2 id="popup-question-title" style={{ margin: 0, paddingRight: '2rem', color: '#173c4d', fontSize: '1.35rem' }}>{displayTitle}</h2>
        </div>
        {showQuestionText && <p style={{ margin: '0 0 1.25rem', color: '#253943', lineHeight: 1.6 }}>{question.question}</p>}
        {question.information && <p style={{ margin: '0 0 1.25rem', color: '#405560', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{question.information}</p>}

        {(question.graphicImageUrl || graphic) && (
          <figure style={{ margin: '0 0 1.25rem', padding: '0.85rem', backgroundColor: '#f5f8f9', border: '1px solid #dce6e9', borderRadius: '7px' }}>
            {graphic?.title && <h3 style={{ margin: '0 0 0.65rem', color: '#254958', fontSize: '1rem' }}>{graphic.title}</h3>}
            {question.graphicImageUrl && (
              <button type="button" onClick={() => setIsGraphicExpanded(true)} aria-label="Enlarge question image" title="Click to enlarge" style={{ display: 'block', width: '100%', padding: 0, border: 0, borderRadius: '4px', background: 'transparent', cursor: 'zoom-in' }}>
                <img src={question.graphicImageUrl} alt={graphic?.altText || graphic?.title || 'Question illustration'} style={{ display: 'block', width: '100%', maxHeight: '300px', objectFit: 'contain', borderRadius: '4px' }} />
              </button>
            )}
            {Array.isArray(graphic?.nodes) && graphic.nodes.length > 0 && (
              <div style={{ display: 'grid', gridTemplateColumns: graphic.layout === 'comparison' ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.6rem', marginTop: question.graphicImageUrl ? '0.75rem' : 0 }}>
                {graphic.nodes.map((node, index) => (
                  <div key={`${node.label || 'detail'}-${index}`} style={{ minWidth: 0, padding: '0.65rem', backgroundColor: 'white', border: '1px solid #dce6e9', borderRadius: '5px' }}>
                    {node.label && <div style={{ marginBottom: '0.25rem', color: '#254958', fontWeight: 700 }}>{node.label}</div>}
                    {node.detail && <div style={{ color: '#526671', fontSize: '0.88rem', lineHeight: 1.45 }}>{node.detail}</div>}
                  </div>
                ))}
              </div>
            )}
            {graphic?.caption && <figcaption style={{ marginTop: '0.65rem', color: '#647780', fontSize: '0.8rem', lineHeight: 1.4 }}>{graphic.caption}</figcaption>}
          </figure>
        )}

        {submittedAnswer ? (
          <div role="status" style={{ padding: '1rem', color: submittedAnswer.isCorrect === true ? '#155724' : submittedAnswer.isCorrect === false ? '#842029' : '#344852', backgroundColor: submittedAnswer.isCorrect === true ? '#d4edda' : submittedAnswer.isCorrect === false ? '#f8d7da' : '#eef3f5', border: `1px solid ${submittedAnswer.isCorrect === true ? '#b8d9c2' : submittedAnswer.isCorrect === false ? '#efc2c7' : '#d7e1e5'}`, borderRadius: '6px' }}>
            <strong style={{ display: 'block', marginBottom: '0.6rem', fontSize: '1.05rem' }}>
              {submittedAnswer.isCorrect === true ? 'Correct' : submittedAnswer.isCorrect === false ? 'Not quite' : 'Answer saved; grading unavailable'}
            </strong>
            {submittedAnswer.correctAnswer && <p style={{ margin: '0 0 0.6rem', lineHeight: 1.55 }}><strong>Correct answer:</strong> {submittedAnswer.correctAnswer}</p>}
            {submittedAnswer.answerFeedback && <p style={{ margin: 0, lineHeight: 1.55 }}>{submittedAnswer.answerFeedback}</p>}
          </div>
        ) : requiresAnswer && question.answerType === 'multiple_choice' ? (
          <fieldset style={{ display: 'grid', gap: '0.55rem', margin: 0, padding: 0, border: 0 }}>
            <legend style={{ marginBottom: '0.6rem', color: '#526671', fontSize: '0.9rem' }}>Choose one answer</legend>
            {[1, 2, 3, 4].map((choiceNumber) => (
              <label key={choiceNumber} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.65rem', padding: '0.7rem 0.8rem', border: `1px solid ${selectedChoice === String(choiceNumber) ? '#367f93' : '#d7e0e4'}`, borderRadius: '6px', backgroundColor: selectedChoice === String(choiceNumber) ? '#eff7f9' : '#fff', cursor: 'pointer' }}>
                <input type="radio" name="popup-question-answer" value={choiceNumber} checked={selectedChoice === String(choiceNumber)} onChange={(event) => setSelectedChoice(event.target.value)} />
                <span>{question[`answerChoice${choiceNumber}`]}</span>
              </label>
            ))}
          </fieldset>
        ) : requiresAnswer ? (
          <div>
            {question.answerType === 'short_answer' && (
              <label htmlFor="popup-question-answer" style={{ display: 'block', marginBottom: '0.5rem', color: '#526671', fontSize: '0.9rem' }}>
                Short answer
              </label>
            )}
            <textarea
              id="popup-question-answer"
              value={answer}
              onChange={(event) => setAnswer(event.target.value)}
              maxLength={question.answerType === 'short_answer' ? 2000 : 10000}
              rows={question.answerType === 'short_answer' ? 3 : 6}
              style={{ width: '100%', padding: '0.75rem', resize: 'vertical', border: '1px solid #cbd6dc', borderRadius: '6px', font: 'inherit', boxSizing: 'border-box' }}
            />
          </div>
        ) : null}

        {errorMessage && <div role="alert" style={{ marginBottom: '1rem', padding: '0.65rem 0.8rem', color: '#842029', backgroundColor: '#f8d7da', borderRadius: '5px' }}>{errorMessage}</div>}
        {submittedAnswer ? (
          <p style={{ margin: '1rem 0 0', color: '#6b7c86', fontSize: '0.85rem' }}>This dialog closes automatically in {answerSecondsRemaining} seconds.</p>
        ) : (
        <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', gap: '0.6rem' }}>
          <button type="button" onClick={optOut} disabled={isSubmitting} style={{ padding: '0.65rem 0.9rem', color: '#53656f', backgroundColor: '#fff', border: '1px solid #b9c5cb', borderRadius: '5px', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>Opt Out</button>
          <div style={{ display: 'flex', gap: '0.6rem', marginLeft: 'auto' }}>
            <button type="button" onClick={dismissQuestion} disabled={isSubmitting} style={{ padding: '0.65rem 1rem', color: '#344852', backgroundColor: '#f1f4f5', border: '1px solid #d4dde1', borderRadius: '5px', cursor: isSubmitting ? 'not-allowed' : 'pointer' }}>{requiresAnswer ? 'Ignore' : 'Close'}</button>
            {requiresAnswer && <button type="button" onClick={submitAnswer} disabled={!canSubmit || isSubmitting} style={{ padding: '0.65rem 1.1rem', color: '#fff', backgroundColor: !canSubmit || isSubmitting ? '#8199a4' : '#176b85', border: 0, borderRadius: '5px', cursor: !canSubmit || isSubmitting ? 'not-allowed' : 'pointer' }}>{isSubmitting ? 'Submitting…' : 'Submit'}</button>}
          </div>
        </div>
        )}
      </section>
      {isGraphicExpanded && question.graphicImageUrl && (
        <div role="presentation" onClick={(event) => { if (event.target === event.currentTarget) setIsGraphicExpanded(false); }} style={{ position: 'fixed', inset: 0, zIndex: 10001, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem', backgroundColor: 'rgba(10, 22, 29, 0.9)', cursor: 'zoom-out' }}>
          <button type="button" onClick={() => setIsGraphicExpanded(false)} aria-label="Close enlarged question image" title="Close image" style={{ position: 'absolute', top: '1rem', right: '1rem', width: '42px', height: '42px', display: 'grid', placeItems: 'center', color: 'white', backgroundColor: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.5)', borderRadius: '50%', fontSize: '1.8rem', lineHeight: 1, cursor: 'pointer' }}>×</button>
          <img src={question.graphicImageUrl} alt={graphic?.altText || graphic?.title || 'Question illustration'} style={{ maxWidth: '94vw', maxHeight: '90vh', objectFit: 'contain', cursor: 'default' }} />
        </div>
      )}
    </div>
  );
};

export default PopupQuestionManager;
